import { formatNameFirstLast } from '@strata/helper-functions';
import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router';

import { PasswordEntry } from 'components/PasswordEntry';
import { DeleteUserDialog } from 'pages/userManagement/dialogs/DeleteUserDialog';
import { CREATE_ROUTE, UserType } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { filterCompassRoles, handleDuplicateUserToast } from 'services/UserSupport';
import { getCompassUserSchema } from 'services/Validators';

export const CompassUserDetails = () => {
    const { upn } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(`/user-management/${UserType.COMPASS}`);

    const isCreate = upn === CREATE_ROUTE;

    const user = useGet(!isCreate ? `/authorization/compass_users/${upn}` : null);

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentOptions = payerSegments?.map((segment) => ({
        name: segment.segment_name,
        secondaryData: segment.segment_id,
    }));

    const roles = useGet('/authorization/roles/');
    const roleOptions = filterCompassRoles(roles);

    const isLoading = !(isCreate || user) || !(payerSegments && roles);

    return (
        <Formik
            initialValues={{
                upn: user?.upn,
                full_name: user?.full_name,
                payer_segments: (payerSegmentOptions ?? []).filter((segment) =>
                    user?.segment_ids?.includes(segment.secondaryData),
                ),
                role: roleOptions?.find((role) => role.role_name === user?.role_name),
            }}
            enableReinitialize
            validationSchema={getCompassUserSchema(isCreate)}
            onSubmit={async (values) => {
                try {
                    let createResult;
                    const sharedValues = {
                        segment_ids: values.payer_segments.map((segment) => segment.secondaryData),
                        role_name: values.role.role_name,
                    };

                    if (isCreate) {
                        createResult = await post('/authorization/compass_users/', {
                            ...sharedValues,
                            upn: values.upn,
                            given_name: values.given_name,
                            family_name: values.family_name,
                            name: formatNameFirstLast(values.given_name, values.family_name),
                            password: values.password,
                        });
                    } else {
                        await put(`/authorization/compass_users/${values.upn}`, {
                            ...sharedValues,
                            full_name: values.full_name,
                        });
                    }

                    if (createResult?.authzero_user_created === false) {
                        handleDuplicateUserToast(values.upn, UserType.COMPASS);
                    } else {
                        SuccessToast({
                            message: `Compass User: ${values.upn} ${
                                isCreate ? 'created' : 'updated'
                            } successfully.`,
                        });
                    }
                    goHome();
                } catch (err) {
                    handleError('Error saving user.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    isLoading={isLoading}
                    formTitle={`${isCreate ? 'Create' : 'Edit'} Compass User`}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formHeaderRightContent={
                        !isCreate && (
                            <DeleteUserDialog
                                userType={UserType.COMPASS}
                                onDeleteSuccess={goHome}
                            />
                        )
                    }
                    formLeftContent={
                        <>
                            <TextInputFormik
                                label="User Email"
                                id="upn"
                                disabled={!isCreate}
                                required
                            />
                            {isCreate ? (
                                <>
                                    <TextInputFormik label="First Name" id="given_name" required />
                                    <TextInputFormik label="Last Name" id="family_name" required />
                                    <PasswordEntry userType={UserType.COMPASS} />
                                </>
                            ) : (
                                <TextInputFormik label="Full Name" id="full_name" required />
                            )}
                            <SuggestMultiSelectFormik
                                id="payer_segments"
                                label="Payer Segments"
                                required
                                values={payerSegmentOptions}
                            />
                            <SuggestSingleSelectFormik
                                id="role"
                                label="Role"
                                required
                                values={roleOptions}
                                helperText={formik.values.role?.description}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
